'use client';

import type { WalletConnectClickedProperties } from '@/ampli';
import { useConnectWallet, useLogin, usePrivy } from '@privy-io/react-auth';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { useFeature } from '@/hooks/useFeature';

import { MATCHA_ACCOUNT } from '@/utils/featureFlags';
import { MATCHA_ACCOUNT_LOGIN_METHODS } from '@/utils/privy';

import { type ChooserOption, WalletConnectionChooserModal } from './WalletConnectionChooserModal';

/**
 * Origin of a connect click. Reuses the existing `walletConnectClicked` source
 * vocabulary so the chooser's gated analytics stay aligned with the events call
 * sites already emit, rather than introducing a parallel enum.
 */
export type WalletConnectSource = WalletConnectClickedProperties['source'];

export type StartConnectFlowArgs = {
  /** Origin of the click; forwarded to the gated chooser analytics. */
  source: WalletConnectSource;
  /** Fires once a connect/login the provider initiated resolves successfully. */
  onSuccess?: () => void;
  /** Restrict Privy's external-wallet modal to a chain family (e.g. EVM-only). */
  walletChainType?: 'ethereum-only' | 'solana-only' | 'ethereum-and-solana';
};

export type WalletConnectionModalContextValue = {
  /**
   * Single entrypoint for every connect surface. Flag off → straight to Privy's
   * external-wallet modal. Flag on → opens the chooser; the picked option routes
   * to Privy's external-wallet modal or login modal.
   */
  startConnectFlow: (args: StartConnectFlowArgs) => void;
};

export const WalletConnectionModalContext = createContext<
  WalletConnectionModalContextValue | undefined
>(undefined);

/**
 * Single funnel for the chooser's Amplitude events. Intentionally no-ops for now.
 *
 * TODO(MAT-6638): wire `matchaAccountChooserShown{source}` and
 * `matchaAccountChooserOptionSelected{option, source}` once the events are
 * approved and synced via ampli. Do NOT call `ampli.*` until then.
 */
const emitChooserShown = (_source: WalletConnectSource): void => {};
const emitChooserOptionSelected = (
  _option: ChooserOption,
  _source: WalletConnectSource,
): void => {};

export function WalletConnectionModalProvider({ children }: { children: ReactNode }) {
  const matchaAccountEnabled = useFeature(MATCHA_ACCOUNT);
  const { isModalOpen } = usePrivy();
  const [isChooserOpen, setIsChooserOpen] = useState(false);

  // The flow that opened the chooser. Held until an option is picked so the
  // handlers know which `source`/`onSuccess` to use; cleared on dismiss.
  const pendingFlow = useRef<StartConnectFlowArgs | null>(null);

  // Privy fans these callbacks out to every consumer, so the ref gates the
  // callback to flows this provider started and fires it once. runOnSuccess
  // nulls it synchronously on success; the modal-close effect below clears a
  // callback abandoned by a dismissed modal, so a later success started by
  // another consumer (Privy is app-wide) can't fire this stale callback.
  const consumeOnSuccess = useRef<(() => void) | null>(null);
  const runOnSuccess = useCallback(() => {
    const cb = consumeOnSuccess.current;
    consumeOnSuccess.current = null;
    cb?.();
  }, []);

  const { connectWallet } = useConnectWallet({ onSuccess: runOnSuccess });
  const { login } = useLogin({ onComplete: runOnSuccess });

  // When the Privy modal closes without success the ref still holds the
  // dismissed flow's callback; drop it so it can't fire on an unrelated later
  // success. A success already nulled the ref synchronously, so this is a no-op
  // there.
  useEffect(() => {
    if (!isModalOpen) consumeOnSuccess.current = null;
  }, [isModalOpen]);

  const openExternalWalletModal = useCallback(
    (args: StartConnectFlowArgs) => {
      consumeOnSuccess.current = args.onSuccess ?? null;
      connectWallet(args.walletChainType ? { walletChainType: args.walletChainType } : undefined);
    },
    [connectWallet],
  );

  const startConnectFlow = useCallback(
    (args: StartConnectFlowArgs) => {
      // Flag off: preserve today's behavior — no chooser, straight to Privy.
      if (!matchaAccountEnabled) {
        openExternalWalletModal(args);
        return;
      }
      pendingFlow.current = args;
      emitChooserShown(args.source);
      setIsChooserOpen(true);
    },
    [matchaAccountEnabled, openExternalWalletModal],
  );

  const handleSelect = useCallback(
    (option: ChooserOption) => {
      const flow = pendingFlow.current;
      pendingFlow.current = null;
      setIsChooserOpen(false);
      if (!flow) return;

      emitChooserOptionSelected(option, flow.source);

      if (option === 'wallet') {
        openExternalWalletModal(flow);
        return;
      }
      // Override the global wallet-only config: the Matcha-account path is
      // email + socials only. login() then makes the embedded EVM + Solana
      // wallets canonical and AuthProvider bootstraps once it resolves.
      consumeOnSuccess.current = flow.onSuccess ?? null;
      login({ loginMethods: MATCHA_ACCOUNT_LOGIN_METHODS });
    },
    [login, openExternalWalletModal],
  );

  const handleClose = useCallback(() => {
    pendingFlow.current = null;
    setIsChooserOpen(false);
  }, []);

  const value = useMemo<WalletConnectionModalContextValue>(
    () => ({ startConnectFlow }),
    [startConnectFlow],
  );

  return (
    <WalletConnectionModalContext.Provider value={value}>
      {children}
      <WalletConnectionChooserModal
        isOpen={isChooserOpen}
        onClose={handleClose}
        showMatchaAccount={matchaAccountEnabled}
        onSelect={handleSelect}
      />
    </WalletConnectionModalContext.Provider>
  );
}
